/**
 * KPI 스트립 — 첫 화면의 답 (DESIGN §7.2 `/metrics/summary`).
 *
 * **가동률을 3모드 동시에 보여준다.** 이 프로젝트의 첫 번째 발견이
 * "단일 고장률 숫자는 존재하지 않는다"(§15.3)인데, 화면이 하나의 큰 숫자로
 * 시작하면 그 발견을 지워 버린다. 세 값이 나란히 있어야 사용자가
 * "정의에 따라 99% 도 되고 18% 도 된다"를 즉시 본다.
 */

import { useQueries } from '@tanstack/react-query'

import { fetchEnvelope } from '@/lib/api'
import { MODES, MODE_HINT, MODE_LABEL, type Envelope, type Mode, type SummaryData } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'
import './KpiStrip.css'

function pct(v: number | null | undefined, digits = 1): string {
  return v == null ? '—' : `${(v * 100).toFixed(digits)}%`
}

export function KpiStrip() {
  const filters = useFilters()
  const key = filterKey(filters)

  const results = useQueries({
    queries: MODES.map((mode) => ({
      queryKey: ['metrics/summary', mode, key],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchEnvelope<SummaryData>(
          '/metrics/summary',
          { ...toQueryFilters(filters), mode },
          {},
          signal,
        ),
      staleTime: 5 * 60 * 1000,
    })),
  })

  const isLoading = results.some((r) => r.isLoading)
  const error = results.find((r) => r.error)?.error as Error | undefined
  const byMode = new Map<Mode, Envelope<SummaryData>>()
  MODES.forEach((m, i) => {
    const d = results[i]?.data as Envelope<SummaryData> | undefined
    if (d) byMode.set(m, d)
  })

  const active = byMode.get(filters.mode)
  const coverage = active?.meta.coverage ?? null
  // **`meta.population_n` 은 전역이라 필터를 타지 않는다.** 필터를 걸어도 이
  // 숫자만 그대로 있으면 화면이 "필터가 적용되지 않았다"고 말하는 셈이다.
  // 필터를 탄 수는 `data.n_chargers` 다 (§8.6.10).
  const population = active?.meta.population_n ?? null
  const scoped = active?.data.n_chargers ?? null
  const isFiltered = population != null && scoped != null && scoped !== population

  if (error) {
    return (
      <section className="kpi kpi--error" role="alert">
        데이터를 불러오지 못했습니다. 서버 상태를 확인하거나 다시 시도하세요.
        <span className="kpi__errdetail">{error.message}</span>
      </section>
    )
  }

  return (
    <section className="kpi" aria-label="핵심 지표" aria-busy={isLoading}>
      <div className="kpi__modes">
        <h2 className="kpi__label">
          관측 가동률
          <span className="kpi__sub">정의에 따라 이만큼 달라진다</span>
        </h2>
        <div className="kpi__modegrid">
          {MODES.map((m) => {
            const env = byMode.get(m)
            const a = env?.data.availability ?? null
            const isActive = m === filters.mode
            return (
              <button
                key={m}
                type="button"
                className={`kpi__mode${isActive ? ' is-active' : ''}`}
                onClick={() => filters.setMode(m)}
                title={MODE_HINT[m]}
                aria-pressed={isActive}
              >
                <span className="kpi__modename">{MODE_LABEL[m]}</span>
                <span className="kpi__modeval num">{pct(a, 1)}</span>
                {env && (
                  <span className="kpi__modeci num">
                    {pct(env.data.availability_ci[0], 1)}–{pct(env.data.availability_ci[1], 1)}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* dl 의 직계 자식은 dt·dd·div 만 허용된다 — 설명문은 dd 안에 넣는다 */}
      <dl className="kpi__facts">
        <div className="kpi__fact">
          <dt>관측 커버리지</dt>
          <dd>
            <span className="kpi__factval num">{pct(coverage, 1)}</span>
            <span className="kpi__factnote">
              나머지는 <strong>관측되지 않은 시간</strong>입니다 — 0 이 아니라 모름
            </span>
          </dd>
        </div>
        <div className="kpi__fact">
          <dt>{isFiltered ? '이 화면의 충전기' : '분석 모집단'}</dt>
          <dd>
            <span className="kpi__factval num">
              {scoped == null ? '—' : scoped.toLocaleString('ko-KR')}
            </span>
            <span className="kpi__factnote">
              {isFiltered ? (
                <>
                  필터를 적용한 수입니다 — 전체 분석 모집단{' '}
                  {population?.toLocaleString('ko-KR')}기 중
                </>
              ) : (
                '전체 523,193기에서 철거·좌표오류·미관측을 뺀 수'
              )}
            </span>
          </dd>
        </div>
        <div className="kpi__fact">
          <dt>이용률</dt>
          <dd>
            <span className="kpi__factval num">{pct(active?.data.utilization, 1)}</span>
            <span className="kpi__factnote">관측 시간 중 충전중(stat=3) 비중 — 수요 지표</span>
          </dd>
        </div>
      </dl>
    </section>
  )
}
